import { 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle,
    Box,
    Typography,
    Stack,
    TextField,
    Chip,
    Alert
} from "@mui/material";
import { Warning, Security, Email, Timer, Refresh } from "@mui/icons-material";
import { useState, useEffect, useRef } from "react";
import { useClassroomActions } from "../../hooks/useClassroomActions";
import { useSnackbar } from "../../components/snackbar";

interface WithdrawalConfirmationDialogProps {
    classroom_id: number;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onProceed: (otp: string) => void;
    amount: string;
}

export default function WithdrawalConfirmationDialog({ 
    classroom_id,
    open, 
    setOpen, 
    onProceed,
    amount
}: WithdrawalConfirmationDialogProps) {
    const { sendWithdrawalOtp } = useClassroomActions();

    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
    const [resendTimer, setResendTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    
    const { showMessage } = useSnackbar();
    
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleClose = () => {
        setOpen(false);
        setOtp(Array(6).fill(''));
        setResendTimer(60);
        setCanResend(false);
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) return; // Only allow single digit
        if (!/^\d*$/.test(value)) return; // Only allow numbers

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
        
        if (pastedData.length === 6) {
            setOtp(pastedData.split(''));
            inputRefs.current[5]?.focus();
        }
    };

    const handleProceed = () => {
        const otpValue = otp.join('');
        
        if (otpValue.length !== 6) {
            showMessage('Please enter complete 6-digit OTP', 2);
            return;
        }

        handleClose();

        setLoading(true);
        onProceed(otpValue);
        setLoading(false);
    };

    const handleResendOtp = async () => {
        await sendWithdrawalOtp(classroom_id, amount);
        
        setResendTimer(60);
        setCanResend(false);
    };

    // Timer effect
    useEffect(() => {
        let interval: NodeJS.Timeout;
        
        if (open && resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(prev => {
                    if (prev === 1) {
                        setCanResend(true);
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [open, resendTimer]);

    // Reset timer when dialog opens
    useEffect(() => {
        if (open) {
            setResendTimer(30);
            setCanResend(false);
            setOtp(Array(6).fill(''));
        }
    }, [open]);

    const isOtpComplete = otp.every(digit => digit !== '');

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="withdrawal-dialog-title"
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    overflow: 'visible',
                    p: 1
                }
            }}
        >
            <DialogTitle 
                id="withdrawal-dialog-title"
                sx={{ 
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#1e293b',
                    textAlign: 'center',
                    pt: 3,
                    pb: 1
                }}
            >
                Verify Withdrawal
            </DialogTitle>

            <DialogContent sx={{ px: 4, pb: 2 }}>
                {/* OTP Instructions */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Email sx={{ fontSize: 24, color: '#6b7280', mb: 1 }} />
                    <Typography variant="body1" color="text.primary" gutterBottom>
                        Enter 6-digit OTP sent to your email
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Check your email for the verification code
                    </Typography>
                </Box>

                {/* OTP Input */}
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                    {otp.map((digit, index) => (
                        <TextField
                            key={index}
                            inputRef={el => inputRefs.current[index] = el}
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={index === 0 ? handlePaste : undefined}
                            inputProps={{
                                maxLength: 1,
                                style: { 
                                    textAlign: 'center', 
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold'
                                }
                            }}
                            sx={{
                                width: 50,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&.Mui-focused': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#3b82f6',
                                            borderWidth: 2
                                        }
                                    }
                                }
                            }}
                        />
                    ))}
                </Stack>


                {/* Resend OTP */}
                <Box sx={{ textAlign: 'center' }}>
                    {!canResend ? (
                        <Chip 
                            icon={<Timer />}
                            label={`Resend OTP in ${resendTimer}s`}
                            variant="outlined"
                            size="small"
                            sx={{ color: '#6b7280' }}
                        />
                    ) : (
                        <Button
                            startIcon={<Refresh />}
                            onClick={handleResendOtp}
                            variant="text"
                            size="small"
                            sx={{ 
                                textTransform: 'none',
                                color: '#3b82f6'
                            }}
                        >
                            Resend OTP
                        </Button>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 4, pb: 3, pt: 1, gap: 2, justifyContent: 'center' }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={loading}
                    sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 4,
                        py: 1.5,
                        borderColor: '#d1d5db',
                        color: '#6b7280',
                        '&:hover': {
                            borderColor: '#9ca3af',
                            backgroundColor: '#f9fafb'
                        }
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={handleProceed}
                    variant="contained"
                    disabled={loading || !isOtpComplete}
                    sx={{ 
                        backgroundColor: "#3b82f6",
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 6,
                        py: 1.5,
                        boxShadow: '0 4px 12px -2px rgba(59, 130, 246, 0.3)',
                        '&:hover': {
                            backgroundColor: "#2563eb",
                            boxShadow: '0 8px 20px -4px rgba(59, 130, 246, 0.4)',
                            transform: 'translateY(-1px)'
                        },
                        '&:disabled': {
                            backgroundColor: "#94a3b8",
                            color: '#ffffff'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    {loading ? 'Verifying...' : 'Confirm Withdrawal'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}